/**
 * useWorkspaceArtefactDiscovery.js — Bundle U-9.A
 * ============================================================================
 *
 * Coordinated mount-time existing-artefact discovery for the candidate
 * workspace. Fires the three by-job lookups in parallel on workspace open
 * (CV variant, Cover Letter, Interview Prep) and exposes a single
 * `lookupStatus` that the three sections + the workspace shell use to:
 *
 *   1. Gate Generate buttons until lookup completes (closes the
 *      pre-U-9 race window where a click between mount and lookup
 *      resolution dispatched a generation the user did not consciously
 *      want).
 *   2. Show a single "Checking for existing application materials…"
 *      surface above the section cards while discovery is in flight.
 *   3. Auto-bind found artefacts by stamping the canonical URL handle
 *      (`?last_variant_id` / `?cover_letter_id` / `?interview_prep_id`)
 *      so each section's existing URL-bound rehydrate effect picks
 *      them up via the canonical hydrator path — NO new hydrator code
 *      is added, and the existing per-section U-2.* fallbacks remain
 *      as defence-in-depth (idempotent).
 *
 * READ-ONLY. NEVER dispatches. NEVER charges. RLS-scoped per JWT.
 *
 * Strict-rail:
 *   • Three parallel `Promise.allSettled` fetches — a single failure
 *     does not block the others.
 *   • `lookupStatus` transitions exactly once per mount:
 *       'idle' → 'checking' → 'ready'
 *   • Re-fires on (jobId, userId) change (workspace navigation),
 *     skipping when either is falsy.
 *   • Defensive try/catch on URL stamp; a missing setSearchParams
 *     (test harness, lazy mount) is a silent no-op.
 *
 * Contract returned to consumers:
 *   {
 *     lookupStatus,          // 'idle' | 'checking' | 'ready'
 *     loadedFromHistory: {   // ← drives the "Loaded from history" chip
 *       cv:           bool,
 *       coverLetter:  bool,
 *       interviewPrep:bool,
 *     },
 *     resultMeta: {          // ← optional: surface IDs for tests
 *       cvVariantId, coverLetterId, interviewPrepId
 *     },
 *   }
 *
 * Consumed by:
 *   • pages/ApplicationWorkspacePage.jsx (host)
 *   • components/workspace/{CvSection,CoverLetterSection,
 *                          InterviewPrepSection}.jsx (gate Generate)
 */

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { apiFetch } from '@/lib/api';

const INITIAL_LOADED = Object.freeze({
  cv:            false,
  coverLetter:   false,
  interviewPrep: false,
});

const INITIAL_META = Object.freeze({
  cvVariantId:     null,
  coverLetterId:   null,
  interviewPrepId: null,
});

export function useWorkspaceArtefactDiscovery({
  jobId,
  userId,
  // ── U-12.FE.B (2026-02-15) — context-aware filtering ────────────
  // `sourceCvId` + `sourceCvType` scope the by-job lookups to the
  // currently-selected CV context. When the user picks a tailored
  // variant, only CL/IP linked to that variant surface. When the
  // user picks the base CV, only CL/IP generated directly against
  // the base surface. Without these, the hook falls back to the
  // legacy job-only scope (returns the latest by job).
  sourceCvId   = null,
  sourceCvType = null,
  // ── U-13.HOOK (2026-02-15) — primaryCvId fallback ───────────────
  // When the workspace mounts before `selectedCv` has been mirrored
  // to URL (pre-pack first paint, hard refresh racing the CvSection
  // URL-write effect), pass `primaryCvId` so the hook still applies
  // a context filter (defaulting to `(base_cv, primaryCvId)`)
  // instead of going job-only and broadly stamping CL/IP handles
  // that don't belong to the user's effective CV context.
  primaryCvId  = null,
  // `fromPack` signals the workspace was opened via Pack→Workspace
  // (i.e., URL carries explicit document handles). When true, the
  // hook does NOT compete with the URL handles — the URL-bound
  // rehydrate effects in each section are the authoritative bind
  // path. We still resolve `lookupStatus` so the Generate gates
  // unlock, but we do NOT stamp URL handles ourselves.
  fromPack     = false,
} = {}) {
  const [lookupStatus, setLookupStatus]       = useState('idle');
  const [loadedFromHistory, setLoadedFrom]    = useState(INITIAL_LOADED);
  const [resultMeta, setResultMeta]           = useState(INITIAL_META);
  const [searchParams, setSearchParams]       = useSearchParams();

  // Pin the latest searchParams setter into a ref so the parallel
  // fetches see a single coherent URL state at write-time — avoids
  // a 3-way URL race where the last setter wins and clobbers
  // siblings' stamps.
  const setSearchParamsRef = useRef(setSearchParams);
  useEffect(() => { setSearchParamsRef.current = setSearchParams; }, [setSearchParams]);

  // Pin searchParams snapshot too — the parallel fetches mutate the
  // same URLSearchParams instance via a builder.
  const searchParamsRef = useRef(searchParams);
  useEffect(() => { searchParamsRef.current = searchParams; }, [searchParams]);

  useEffect(() => {
    if (!jobId || !userId) {
      setLookupStatus('idle');
      setLoadedFrom(INITIAL_LOADED);
      setResultMeta(INITIAL_META);
      return undefined;
    }

    let cancelled = false;
    setLookupStatus('checking');

    // U-12.FE.B — build query-string with source-context filter.
    // Backend `by-job` endpoints accept `?cv_variant_id=` and
    // `?base_cv_id=` to filter by canonical column values (works
    // even on legacy rows where `content_json.job_id` is null).
    //
    // U-13.HOOK (2026-02-15) — when the URL hasn't yet stamped
    // `?source_cv_type` / `?source_cv_id` (cold workspace mount,
    // pre-pack first paint), fall back to `(base_cv, primaryCvId)`.
    // This prevents the no-filter query that previously returned
    // the broadest possible CL/IP for the job — including artefacts
    // tied to historical variants the user is not currently in.
    const _effectiveCvType = sourceCvType
      || (primaryCvId ? 'base_cv' : null);
    const _effectiveCvId   = sourceCvId
      || (sourceCvType ? null : primaryCvId);
    const ctxQs = (() => {
      const p = new URLSearchParams();
      p.set('latest', 'true');
      if (_effectiveCvType === 'cv_variant' && _effectiveCvId) {
        p.set('cv_variant_id', _effectiveCvId);
      } else if (_effectiveCvType === 'base_cv' && _effectiveCvId) {
        p.set('base_cv_id', _effectiveCvId);
      }
      return p.toString();
    })();
    // CV variant lookup uses base_cv filter only (cv_variant filter
    // is meaningless on the variants endpoint).
    const cvCtxQs = (() => {
      const p = new URLSearchParams();
      p.set('latest', 'true');
      if (_effectiveCvType === 'base_cv' && _effectiveCvId) {
        p.set('base_cv_id', _effectiveCvId);
      }
      return p.toString();
    })();

    (async () => {
      // ── Three parallel by-job lookups. ──────────────────────────
      // Each endpoint already returns { found: false, ... } on
      // no-match so we never throw on the happy "no history" case.
      // We accept a hard failure on any single endpoint without
      // blocking the others (Promise.allSettled).
      const [cvR, clR, ipR] = await Promise.allSettled([
        apiFetch(`/api/cv-variants/by-job/${encodeURIComponent(jobId)}?${cvCtxQs}`),
        apiFetch(`/api/cover-letters/by-job/${encodeURIComponent(jobId)}?${ctxQs}`),
        apiFetch(`/api/interview-prep/by-job/${encodeURIComponent(jobId)}?${ctxQs}`),
      ]);
      if (cancelled) return;

      const cvEnv = cvR.status === 'fulfilled' && cvR.value?.found === true
        ? cvR.value : null;
      const clEnv = clR.status === 'fulfilled' && clR.value?.found === true
        ? clR.value : null;
      const ipEnv = ipR.status === 'fulfilled' && ipR.value?.found === true
        ? ipR.value : null;

      const cvId = cvEnv?.cv_variant_id || null;
      const clId = clEnv?.cover_letter_id || null;
      const ipId = ipEnv?.interview_prep_id || ipEnv?.result?.interview_prep_id || null;

      // ── Stamp canonical URL handles atomically. ─────────────────
      // Each section already owns a URL-bound rehydrate effect that
      // binds when its handle is present. Stamping all three here
      // in one URLSearchParams write avoids triggering 3 cascaded
      // history-pushes.
      //
      // U-12.FE.B — when `fromPack=true`, the URL ALREADY carries
      // the pack's explicit document handles (via U-12.FE.A). We
      // do NOT stamp anything here — let the URL-bound rehydrate
      // bind those exact IDs. The pack's composition is the truth.
      //
      // U-11.CV.A — the legacy guard `!next.get('source_cv_id')`
      // is GONE. `last_variant_id` and `source_cv_id` represent
      // ORTHOGONAL state: source = the user's CV selection; last
      // variant = the latest tailored artefact for this (user, job)
      // and currently-bound source context. Stamping the latter
      // when the former is set is correct.
      try {
        const next = new URLSearchParams(searchParamsRef.current);
        let changed = false;
        if (!fromPack) {
          if (cvId && next.get('last_variant_id') !== cvId) {
            next.set('last_variant_id', cvId); changed = true;
          }
          if (clId && next.get('cover_letter_id') !== clId) {
            next.set('cover_letter_id', clId); changed = true;
          }
          if (ipId && next.get('interview_prep_id') !== ipId) {
            next.set('interview_prep_id', ipId); changed = true;
          }
        }
        if (changed && typeof setSearchParamsRef.current === 'function') {
          setSearchParamsRef.current(next, { replace: true });
        }
      } catch (e) {
        // Silent — test harness / lazy mount / SSR. The per-section
        // U-2.* fallbacks will still run and stamp on next tick.
        // eslint-disable-next-line no-console
        console.warn(
          '[useWorkspaceArtefactDiscovery] URL stamp skipped:',
          e?.message || e,
        );
      }

      setLoadedFrom({
        cv:            !!cvEnv,
        coverLetter:   !!clEnv,
        interviewPrep: !!ipEnv,
      });
      setResultMeta({
        cvVariantId:     cvId,
        coverLetterId:   clId,
        interviewPrepId: ipId,
      });
      setLookupStatus('ready');
    })();

    return () => { cancelled = true; };
  }, [jobId, userId, sourceCvId, sourceCvType, primaryCvId, fromPack]);

  return { lookupStatus, loadedFromHistory, resultMeta };
}

export default useWorkspaceArtefactDiscovery;
